/**
 * Axios Instance with JWT Interceptors
 * Automatically handles token refresh and error responses
 */

import axios from 'axios';
import { MAINAPIURL } from '../pages/appsettings';
import { getAccessToken, refreshAccessToken, clearAuthData } from './authService';

// Create axios instance
const axiosInstance = axios.create({
  baseURL: MAINAPIURL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Flag to prevent multiple refresh attempts
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Request interceptor - Add Authorization header
axiosInstance.interceptors.request.use(
  (config) => {
    const token = getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - Handle 401 errors and token refresh
axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // If error is 401 and we haven't retried yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Check if it's a token expired error
      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return axiosInstance(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const newToken = await refreshAccessToken();
        processQueue(null, newToken);
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        // Redirect to login if refresh fails
        clearAuthData();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // BREAKUP LOOP: If it's 401 AND we already retried, it means the refreshed token is also invalid.
    // Force logout to prevent any potential loops or broken states.
    if (error.response?.status === 401 && originalRequest._retry) {
      clearAuthData();
      window.location.href = '/login';
      return Promise.reject(error);
    }

    // Handle 403 Forbidden - backstop for stale permissions: show the Access Denied page
    if (error.response?.status === 403) {
      console.error('Access denied:', error.response?.data?.message);
      if (window.location.pathname !== '/access-denied') {
        window.location.href = '/access-denied';
      }
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
